import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../cn.js';

const control =
  'w-full rounded-lg border border-border bg-surface px-3 text-sm text-text placeholder:text-subtle shadow-panel ' +
  'transition-[border-color,box-shadow] hover:border-border-strong focus:border-accent focus:outline-none ' +
  'focus:ring-4 focus:ring-accent/15 disabled:opacity-60 aria-[invalid=true]:border-danger';

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(control, 'h-9', className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(control, 'min-h-20 py-2', className)} {...props} />;
}

export interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: string;
  className?: string;
  /** Render prop receives the generated id and aria attributes for the control. */
  children: (props: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby'?: string;
  }) => ReactNode;
}

/** Label + control + error/hint, wired together for accessibility. */
export function Field({ label, error, hint, className, children }: FieldProps) {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-[13px] font-medium text-text">
        {label}
      </label>
      {children({
        id,
        'aria-invalid': Boolean(error),
        ...(message ? { 'aria-describedby': messageId } : {}),
      })}
      {message && (
        <p id={messageId} className={cn('text-xs', error ? 'text-danger' : 'text-muted')}>
          {message}
        </p>
      )}
    </div>
  );
}
